/**
 * Shared logic for entity and single form generators
 */

import { isLayoutField } from '@core-engine/schema/constants.js'
import type { GeneratedFile, SchemaField } from '@core-engine/schema/types.js'
import { quotePropertyName, toPascalCase } from '@core-engine/utils/string.js'
import { getZodType } from '@next-adapter/generators/form/zod-schema.js'

export interface FormGeneratorResult {
  files: GeneratedFile[]
}

function hasVisibleLabel(field: SchemaField): boolean {
  return typeof field.label === 'string' ? field.label.trim().length > 0 : Boolean(field.label)
}

export function formLabel(field: SchemaField, text: string): string {
  return hasVisibleLabel(field)
    ? `<FormLabel>${text}</FormLabel>`
    : `<FormLabel className="sr-only">${text}</FormLabel>`
}

export function formItemProps(field: SchemaField, className?: string): string {
  if (field.height === 'fill' && className) return ` className="flex-1 min-h-0 ${className}"`
  if (field.height === 'fill') return ' className="flex-1 min-h-0"'
  return className ? ` className="${className}"` : ''
}

export function fillControlClass(field: SchemaField, className: string): string {
  return field.height === 'fill' ? `${className} flex-1` : className
}

export function collectRelationshipFields(
  fields: SchemaField[],
  _tabFieldNames: Set<string>
): SchemaField[] {
  const result: SchemaField[] = []
  const seen = new Set<string>()
  function collect(fieldsToCheck: SchemaField[]): void {
    for (const f of fieldsToCheck) {
      if (f.type === 'relationship' && f.relationship && !seen.has(f.name)) {
        seen.add(f.name)
        result.push(f)
      }
      if ((f.type === 'group' || f.type === 'section') && f.fields) collect(f.fields)
      if (f.type === 'tabs' && f.tabs) {
        for (const tab of f.tabs) {
          if (tab.fields) collect(tab.fields)
        }
      }
    }
  }
  collect(fields)
  return result
}

function getTabNestedFields(tab: NonNullable<SchemaField['tabs']>[number]): SchemaField[] {
  return [
    ...(tab.fields ?? []),
    ...(tab.slot?.main?.fields ?? []),
    ...(tab.slot?.sidebar?.fields ?? [])
  ]
}

export function collectTabIconNames(fields: SchemaField[]): string[] {
  const icons = new Set<string>()

  function collect(fieldsToCheck: SchemaField[]): void {
    for (const field of fieldsToCheck) {
      if (field.fields) collect(field.fields)
      if (field.tabs) {
        for (const tab of field.tabs) {
          const icon = tab.icon?.trim()
          if (icon) icons.add(toPascalCase(icon))
          collect(getTabNestedFields(tab))
        }
      }
    }
  }

  collect(fields)
  return Array.from(icons)
}

export function renderTabsTrigger(
  tab: NonNullable<SchemaField['tabs']>[number],
  indent: string,
  options: { className?: string } = {}
): string {
  const iconName = tab.icon?.trim() ? toPascalCase(tab.icon.trim()) : null
  const className = options.className ? ` className="${options.className}"` : ''
  if (!iconName)
    return `${indent}<TabsTrigger value="${tab.name}"${className}>${tab.label}</TabsTrigger>`

  return `${indent}<TabsTrigger value="${tab.name}"${className}>
${indent}  <${iconName} className="size-4 shrink-0" />
${indent}  <span>${tab.label}</span>
${indent}</TabsTrigger>`
}

function selectDefaultValues(f: SchemaField): string[] {
  if (f.default === undefined || f.default === null || f.default === '') return []
  const values = Array.isArray(f.default) ? f.default : [f.default]
  return values.map((value) => String(value))
}

interface DefaultValueFromTarget {
  name: string
  sourceName: string
}

function buildFieldMap(fields: SchemaField[]): Map<string, SchemaField> {
  const fieldMap = new Map<string, SchemaField>()
  for (const field of fields) {
    if (!field.primaryKey && field.type !== 'tabs' && !isLayoutField(field.type)) {
      fieldMap.set(field.name, field)
    }
  }
  return fieldMap
}

function collectDefaultValueFromTargets(fields: SchemaField[]): DefaultValueFromTarget[] {
  const fieldMap = buildFieldMap(fields)
  return fields.flatMap((field) => {
    const sourceName = field.defaultValueFrom?.trim()
    if (
      !sourceName ||
      field.primaryKey ||
      field.type === 'tabs' ||
      isLayoutField(field.type) ||
      sourceName === field.name ||
      !fieldMap.has(sourceName)
    ) {
      return []
    }

    return [{ name: field.name, sourceName }]
  })
}

export function defaultValueFromHandlerName(sourceName: string): string {
  return `applyDefaultValueFrom${toPascalCase(sourceName)}`
}

export function collectDefaultValueFromSourceNames(fields: SchemaField[]): Set<string> {
  return new Set(collectDefaultValueFromTargets(fields).map((target) => target.sourceName))
}

function defaultFallbackValue(f: SchemaField): string {
  if (f.type === 'list' || f.type === 'gallery') return '[]'
  if ((f.type === 'group' || f.type === 'section') && f.fields?.length) {
    const values = f.fields.flatMap((nestedField) => {
      const entries = [
        `${quotePropertyName(nestedField.name)}: ${defaultFallbackValue(nestedField)}`
      ]
      if (nestedField.hasIcon) {
        entries.push(`${quotePropertyName(`${nestedField.name}Icon`)}: ''`)
      }
      return entries
    })
    return `{ ${values.join(', ')} }`
  }
  if (f.type === 'boolean') return f.default === true ? 'true' : 'false'
  if (f.type === 'number' || f.type === 'decimal') return f.required ? '0' : 'undefined'
  if (f.type === 'uuid' || f.type === 'serial' || f.type === 'relationship') {
    return f.multiple ? '[]' : "''"
  }
  if (f.type === 'date' || f.type === 'timestamp') return 'undefined'
  if (f.type === 'select') {
    if (f.multiple) return JSON.stringify(selectDefaultValues(f))
    return f.default !== undefined && f.default !== null ? JSON.stringify(f.default) : "''"
  }
  if (f.default !== undefined && f.default !== null) {
    return typeof f.default === 'string' ? JSON.stringify(f.default) : String(f.default)
  }
  return "''"
}

function propertyAccessExpression(objectName: string, fieldName: string): string {
  return /^[A-Za-z_$][\w$]*$/.test(fieldName)
    ? `${objectName}?.${fieldName}`
    : `${objectName}?.[${JSON.stringify(fieldName)}]`
}

function generateNestedFieldDefaultExpression(
  field: SchemaField,
  dataSource: string,
  depth = 1
): string {
  const fieldSource = propertyAccessExpression(dataSource, field.name)

  if ((field.type === 'group' || field.type === 'section') && field.fields?.length) {
    const values = field.fields.flatMap((nestedField) => {
      const fieldDefault = `${quotePropertyName(nestedField.name)}: ${generateNestedFieldDefaultExpression(nestedField, fieldSource, depth)}`
      if (!nestedField.hasIcon) return [fieldDefault]

      const iconName = `${nestedField.name}Icon`
      return [
        fieldDefault,
        `${quotePropertyName(iconName)}: ${propertyAccessExpression(fieldSource, iconName)} ?? ''`
      ]
    })

    return `{ ${values.join(', ')} }`
  }

  if (field.type === 'list' && field.fields?.length) {
    const itemName = `nestedItem${depth}`
    const values = field.fields.flatMap((nestedField) => {
      const fieldDefault = `${quotePropertyName(nestedField.name)}: ${generateNestedFieldDefaultExpression(nestedField, itemName, depth + 1)}`
      if (!nestedField.hasIcon) return [fieldDefault]

      const iconName = `${nestedField.name}Icon`
      return [
        fieldDefault,
        `${quotePropertyName(iconName)}: ${propertyAccessExpression(itemName, iconName)} ?? ''`
      ]
    })

    return `(${fieldSource} ?? []).map((${itemName}) => ({ ${values.join(', ')} }))`
  }

  return `${fieldSource} ?? ${defaultFallbackValue(field)}`
}

function generateNestedListDefaultValue(f: SchemaField, dataSource: string): string {
  const nestedFields = f.fields ?? []
  const values = nestedFields.flatMap((nestedField) => {
    const fieldDefault = `        ${quotePropertyName(nestedField.name)}: ${generateNestedFieldDefaultExpression(nestedField, 'item')}`
    if (!nestedField.hasIcon) return [fieldDefault]

    const iconName = `${nestedField.name}Icon`
    return [
      fieldDefault,
      `        ${quotePropertyName(iconName)}: ${propertyAccessExpression('item', iconName)} ?? ''`
    ]
  })

  if (values.length === 0) return `      ${f.name}: ${dataSource}?.${f.name} ?? []`

  return `      ${f.name}: (${dataSource}?.${f.name} ?? []).map((item) => ({
${values.join(',\n')}
      }))`
}

function hasInitialValueExpression(fieldName: string, dataSource = 'initialData'): string {
  return `${dataSource}?.${fieldName} !== undefined && ${dataSource}?.${fieldName} !== null && ${dataSource}?.${fieldName} !== ''`
}

function selectReadValuesExpression(fieldName: string, dataSource = 'initialData'): string {
  return `(() => {
        const selectValue = ${dataSource}?.${fieldName} as unknown
        const values = Array.isArray(selectValue) ? selectValue : [selectValue]
        return values
          .map((option) => {
            if (typeof option === 'string') return option
            if (option && typeof option === 'object' && 'value' in option) {
              return String(option.value)
            }
            return null
          })
          .filter((value): value is string => typeof value === 'string' && value.length > 0)
      })()`
}

export function generateDefaultValue(
  f: SchemaField,
  fieldMap?: Map<string, SchemaField>,
  dataSource = 'initialData',
  options: { slugFallbackValue?: string } = {}
): string {
  if (f.name === 'slug' && !f.hidden && options.slugFallbackValue) {
    return `      ${f.name}: ${dataSource}?.${f.name} ?? '${options.slugFallbackValue}'`
  }

  if (
    f.defaultValueFrom &&
    f.defaultValueFrom.trim() !== f.name &&
    fieldMap?.has(f.defaultValueFrom.trim())
  ) {
    const sourceName = f.defaultValueFrom.trim()
    if (f.type === 'select') {
      const targetValuesExpression = selectReadValuesExpression(f.name, dataSource)
      const sourceValuesExpression = selectReadValuesExpression(sourceName, dataSource)
      if (f.multiple) {
        const fallback = JSON.stringify(selectDefaultValues(f))
        return `      ${f.name}: ${targetValuesExpression}.length > 0
        ? ${targetValuesExpression}
        : ${sourceValuesExpression}.length > 0
          ? ${sourceValuesExpression}
          : ${fallback}`
      }

      const fallback =
        f.default !== undefined && f.default !== null ? JSON.stringify(f.default) : "''"
      return `      ${f.name}: ${targetValuesExpression}[0] ?? ${sourceValuesExpression}[0] ?? ${fallback}`
    }

    return `      ${f.name}: ${hasInitialValueExpression(f.name, dataSource)}
        ? ${dataSource}.${f.name}
        : ${hasInitialValueExpression(sourceName, dataSource)}
          ? (${dataSource}.${sourceName} as never)
          : ${defaultFallbackValue(f)}`
  }
  if (f.type === 'list' && f.fields && f.fields.length > 0) {
    return generateNestedListDefaultValue(f, dataSource)
  }
  if (f.type === 'list' || f.type === 'gallery')
    return `      ${f.name}: ${dataSource}?.${f.name} ?? []`
  if (f.type === 'boolean') {
    const def = f.default === true ? 'true' : 'false'
    return `      ${f.name}: ${dataSource}?.${f.name} ?? ${def}`
  }
  if (f.type === 'number' || f.type === 'decimal') {
    const def = f.required ? '0' : 'undefined'
    return `      ${f.name}: ${dataSource}?.${f.name} ?? ${def}`
  }
  if (f.type === 'uuid' || f.type === 'serial') {
    return `      ${f.name}: ${dataSource}?.${f.name} ?? ''`
  }
  if (f.type === 'relationship') {
    if (f.multiple) return `      ${f.name}: ${dataSource}?.${f.name} ?? []`
    return `      ${f.name}: ${dataSource}?.${f.name}
        ? (typeof ${dataSource}?.${f.name} === 'object' ? String(${dataSource}?.${f.name}.id) : String(${dataSource}?.${f.name}))
        : ''`
  }
  if (f.type === 'date' || f.type === 'timestamp') {
    return `      ${f.name}: ${dataSource}?.${f.name} ?? undefined`
  }
  if (f.type === 'select') {
    const selectedValuesExpression = selectReadValuesExpression(f.name, dataSource)
    if (f.multiple) {
      const fallback = JSON.stringify(selectDefaultValues(f))
      if (f.creatable) {
        return `      ${f.name}: ${selectedValuesExpression}.length > 0 ? ${selectedValuesExpression} : ${fallback}`
      }
      const validValuesExpression = `([${(f.options || []).map((option) => JSON.stringify(option.value)).join(', ')}] as const as readonly string[])`
      return `      ${f.name}: ${selectedValuesExpression}.length > 0
        ? ${selectedValuesExpression}.filter((value) => ${validValuesExpression}.includes(value))
        : ${fallback}`
    }
    const fallback =
      f.default !== undefined && f.default !== null ? JSON.stringify(f.default) : "''"
    if (f.creatable) {
      return `      ${f.name}: ${selectedValuesExpression}[0] ?? ${fallback}`
    }
    const validValuesExpression = `([${(f.options || []).map((option) => JSON.stringify(option.value)).join(', ')}] as const as readonly string[])`
    return `      ${f.name}: ${selectedValuesExpression}[0] && ${validValuesExpression}.includes(${selectedValuesExpression}[0]) ? ${selectedValuesExpression}[0] : ${fallback}`
  }
  if (f.default !== undefined && f.default !== null) {
    const def = typeof f.default === 'string' ? `'${f.default}'` : f.default
    return `      ${f.name}: ${dataSource}?.${f.name} ?? ${def}`
  }
  return `      ${f.name}: ${dataSource}?.${f.name} ?? ''`
}

export function buildDefaultValueFromSyncFunctions(
  fields: SchemaField[],
  options: { slugValueHelperName?: string } = {}
): string {
  const mappings = new Map<string, string[]>()
  for (const target of collectDefaultValueFromTargets(fields)) {
    const targets = mappings.get(target.sourceName) ?? []
    targets.push(target.name)
    mappings.set(target.sourceName, targets)
  }

  return Array.from(mappings.entries())
    .map(([sourceName, targetNames]) => {
      const targetSync = targetNames
        .map((targetName) => {
          const currentValueName = `current${toPascalCase(targetName)}`
          const nextValueExpression =
            targetName === 'slug' && options.slugValueHelperName
              ? `${options.slugValueHelperName}(nextValue)`
              : 'nextValue'
          const previousValueExpression =
            targetName === 'slug' && options.slugValueHelperName
              ? `${options.slugValueHelperName}(previousValue)`
              : 'previousValue'
          const canSyncExpression =
            targetName === 'slug' && options.slugValueHelperName ? '!initialData && ' : ''
          return `    const ${currentValueName} = form.getValues('${targetName}') as unknown
    if (${canSyncExpression}(
      ${currentValueName} === '' ||
      ${currentValueName} === undefined ||
      ${currentValueName} === null ||
      ${currentValueName} === ${previousValueExpression}
    )) {
      form.setValue('${targetName}', ${nextValueExpression} as never, {
        shouldDirty: false,
        shouldValidate: true
      })
    }`
        })
        .join('\n')

      return `  function ${defaultValueFromHandlerName(sourceName)}(nextValue: unknown) {
    const previousValue = form.getValues('${sourceName}') as unknown
${targetSync}
  }`
    })
    .join('\n\n')
}

export function buildZodFields(flatFields: SchemaField[]): string {
  return flatFields
    .filter((f) => !f.primaryKey && f.type !== 'tabs' && !isLayoutField(f.type))
    .flatMap((f) => {
      const defs: string[] = []
      const zodType = getZodType(f)
      const alreadyOptional = zodType.includes('.optional()')
      let def = `  ${quotePropertyName(f.name)}: ${zodType}`
      if (!f.required && !alreadyOptional) def += '.optional()'
      defs.push(def)
      if (f.hasIcon) defs.push(`  ${quotePropertyName(`${f.name}Icon`)}: z.string().optional()`)
      return defs
    })
    .join(',\n')
}

export function buildDefaultValues(
  flatFields: SchemaField[],
  dataSource = 'initialData',
  sourceFields = flatFields,
  options: { slugFallbackValue?: string } = {}
): string {
  const fieldMap = buildFieldMap(sourceFields)
  return flatFields
    .filter((f) => !f.primaryKey && f.type !== 'tabs' && !isLayoutField(f.type))
    .flatMap((f) => {
      const defs = [generateDefaultValue(f, fieldMap, dataSource, options)]
      if (f.hasIcon) defs.push(`      ${f.name}Icon: ${dataSource}?.${f.name}Icon ?? ''`)
      return defs
    })
    .join(',\n')
}

export function buildUiImports(ctx: {
  hasBoolean: boolean
  hasTextarea: boolean
  hasImage: boolean
  hasVideo: boolean
  hasMedia: boolean
  hasGallery: boolean
  hasIcon: boolean
  hasIconPostfix: boolean
  hasDate: boolean
  hasMarkdown: boolean
  hasRichtext: boolean
  hasSeparator: boolean
  hasSelect: boolean
  hasSelectCombobox: boolean
  hasCreatableSelect: boolean
  hasMultiSelect: boolean
  hasTabsField: boolean
  hasRelationship: boolean
  hasSimpleList: boolean
  hasNestedList: boolean
  hasNestedObjectList?: boolean
  hasCardContent?: boolean
  hasSectionCardHeading?: boolean
  hasFormRoot?: boolean
  hasButton?: boolean
  hasFormControls?: boolean
  hasInput?: boolean
}): string[] {
  const cardImports = ['Card']
  if (ctx.hasCardContent || ctx.hasSectionCardHeading) cardImports.push('CardContent')
  if (ctx.hasSectionCardHeading) cardImports.push('CardHeader', 'CardTitle')
  const formImports = [
    ctx.hasFormRoot === false ? '' : 'Form',
    ...(ctx.hasFormControls === false
      ? []
      : ['FormControl', 'FormDescription', 'FormField', 'FormItem', 'FormLabel', 'FormMessage'])
  ].filter(Boolean)

  const uiImports: string[] = [
    `import { ${cardImports.join(', ')} } from '@admin/components/ui/card'`,
    ctx.hasInput === false ? '' : "import { Input } from '@admin/components/ui/input'",
    formImports.length > 0
      ? `import {
  ${formImports.join(',\n  ')}
} from '@admin/components/ui/form'`
      : ''
  ].filter(Boolean)

  if (ctx.hasButton !== false) {
    uiImports.unshift("import { Button } from '@admin/components/ui/button'")
  }

  if (ctx.hasBoolean) uiImports.push("import { Checkbox } from '@admin/components/ui/checkbox'")
  if (ctx.hasTextarea) uiImports.push("import { Textarea } from '@admin/components/ui/textarea'")
  if (ctx.hasImage || ctx.hasVideo || ctx.hasMedia)
    uiImports.push("import { MediaField } from '@admin/components/custom/media-field'")
  if (ctx.hasGallery)
    uiImports.push("import { GalleryField } from '@admin/components/custom/gallery-field'")
  if (ctx.hasIcon || ctx.hasIconPostfix)
    uiImports.push("import { IconPicker } from '@admin/components/custom/icon-picker'")
  if (ctx.hasDate)
    uiImports.push("import { DatePicker } from '@admin/components/custom/date-picker'")
  if (ctx.hasMarkdown || ctx.hasRichtext)
    uiImports.push("import { ContentEditor } from '@admin/components/custom/content-editor'")
  if (ctx.hasSeparator) uiImports.push("import { Separator } from '@admin/components/ui/separator'")
  if (ctx.hasSelect) {
    uiImports.push(`import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from '@admin/components/ui/select'`)
  }
  if (ctx.hasRelationship || ctx.hasSelectCombobox) {
    const commandImports = [
      'Command',
      'CommandEmpty',
      'CommandGroup',
      'CommandInput',
      'CommandItem',
      'CommandList',
      ctx.hasCreatableSelect ? 'CommandSeparator' : ''
    ].filter(Boolean)
    uiImports.push(`import {
  ${commandImports.join(',\n  ')}
} from '@admin/components/ui/command'`)
    uiImports.push(`import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@admin/components/ui/popover'`)
  }
  if (ctx.hasMultiSelect) {
    uiImports.push("import { Badge } from '@admin/components/ui/badge'")
    uiImports.push(`import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@admin/components/ui/tooltip'`)
  }
  if (ctx.hasTabsField) {
    uiImports.push(`import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '@admin/components/ui/tabs'`)
  }
  if (ctx.hasSimpleList)
    uiImports.push("import { DynamicListField } from '@admin/components/custom/dynamic-list-field'")
  if (ctx.hasNestedObjectList)
    uiImports.push(
      "import { NestedObjectListField } from '@admin/components/custom/nested-object-list-field'"
    )
  if (ctx.hasNestedList) {
    uiImports.push(`import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@admin/components/ui/accordion'`)
  }

  return uiImports
}
